import * as React from "react"
import { cn } from "@nexus/ui"
import { PersonaCard, PersonaCardSkeleton } from "../PersonaCard/PersonaCard"
import {
  PERSONA_ENTRANCE_STAGGER_CAP,
  PERSONA_ENTRANCE_STAGGER_MS,
} from "../../constants/personas.constants"
import type { Persona } from "../../types/personas.types"

export const PERSONA_GRID_CLASS = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"

export interface PersonaGridProps {
  personas: Persona[]
  totalSpend: number
  onCreateOpportunitySet?: (persona: Persona) => void
  onOpenDetail?: (persona: Persona) => void
  className?: string
}

export function PersonaGrid({ personas, totalSpend, onCreateOpportunitySet, onOpenDetail, className }: PersonaGridProps) {
  // A card that has already been on screen re-enters at full speed: the stagger is there to
  // orchestrate the first paint, not to make a filter change feel slower than it is.
  const seenPersonaIdsRef = React.useRef(new Set<string>())

  return (
    <div className={cn(PERSONA_GRID_CLASS, className)}>
      {personas.map((persona, index) => {
        const isFirstEntrance = !seenPersonaIdsRef.current.has(persona.id)
        seenPersonaIdsRef.current.add(persona.id)

        return (
          <PersonaCard
            key={persona.id}
            persona={persona}
            totalSpend={totalSpend}
            spendFillDelayMs={
              isFirstEntrance
                ? Math.min(index, PERSONA_ENTRANCE_STAGGER_CAP) * PERSONA_ENTRANCE_STAGGER_MS
                : 0
            }
            onCreateOpportunitySet={onCreateOpportunitySet}
            onOpenDetail={onOpenDetail}
          />
        )
      })}
    </div>
  )
}

export function PersonaGridSkeleton({ count = 8, className }: { count?: number; className?: string }) {
  return (
    <div className={cn(PERSONA_GRID_CLASS, className)}>
      {Array.from({ length: count }).map((_, i) => (
        <PersonaCardSkeleton key={i} />
      ))}
    </div>
  )
}
